import React, { useMemo, useState } from 'react';
import { FaPencil, FaTrash, FaTriangleExclamation, FaCircleCheck } from 'react-icons/fa6';
import Badge from '@/components/Badges';
import {
  MAPPING_FIELDS,
  REQUIRED_MAPPING_FIELDS,
  DELIMITER_OPTIONS,
  describeDelimiter,
  sourceMeta,
} from './constants';

const blankDraft = () =>
  MAPPING_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), { delimiter: '' });

const draftFromMapping = (mapping) =>
  MAPPING_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: mapping?.[f.key] || '' }), {
    delimiter: mapping?.delimiter ?? '',
  });

/**
 * One store's column mapping: which CSV header feeds each canonical inventory
 * column.
 *
 * Every value here is a *header name in the retailer's file*, not a value —
 * sku = "ItemNum" means "read retailer_sku from the ItemNum column".
 */
const ColumnMappingCard = ({ mapping, storeId, onSave, onDelete, saving }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => (mapping ? draftFromMapping(mapping) : blankDraft()));
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const missingRequired = useMemo(
    () => REQUIRED_MAPPING_FIELDS.filter((key) => !(draft[key] || '').trim()),
    [draft]
  );

  const startEdit = () => {
    setDraft(mapping ? draftFromMapping(mapping) : blankDraft());
    setEditing(true);
  };

  const submit = async () => {
    if (missingRequired.length > 0) return;
    const ok = await onSave(draft);
    if (ok) setEditing(false);
  };

  const source = sourceMeta(mapping?.source);
  const resolvable = mapping?.resolvable;

  if (editing) {
    return (
      <div className="bg-white dark:bg-theme-surface-accent border border-slate-200 dark:border-slate-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          {mapping ? 'Edit column mapping' : 'Create column mapping'}
        </h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Enter the <strong>header names as they appear in the retailer&rsquo;s CSV</strong>. Saving
          is a full replace, and it pins the mapping as <em>manual</em> so the pipeline stops
          re-extracting it from Glue.
        </p>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {MAPPING_FIELDS.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {field.label}
                {field.required && <span className="text-rose-500 ml-1">*</span>}
              </label>
              <input
                type="text"
                value={draft[field.key] || ''}
                onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                placeholder={field.required ? 'Required' : 'Leave blank if not in the file'}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{field.help}</p>
            </div>
          ))}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Delimiter
            </label>
            <select
              value={draft.delimiter ?? ''}
              onChange={(e) => setDraft({ ...draft, delimiter: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              {DELIMITER_OPTIONS.map((opt) => (
                <option key={opt.label} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Blank is a real setting, not a gap: the parser auto-detects from the header, which is
              how most production rows are stored.
            </p>
          </div>
        </div>

        {missingRequired.length > 0 && (
          <p className="mt-4 text-xs text-rose-500 dark:text-rose-400">
            {missingRequired.map((k) => k.toUpperCase()).join(' and ')} required. A mapping without
            them is re-extracted from Glue on the store&rsquo;s next file and your edit is lost.
          </p>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={() => setEditing(false)}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || missingRequired.length > 0}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save mapping'}
          </button>
        </div>
      </div>
    );
  }

  if (!mapping) {
    return (
      <div className="bg-white dark:bg-theme-surface-accent border border-slate-200 dark:border-slate-700 rounded-lg p-6 text-center">
        <p className="text-slate-600 dark:text-slate-300">
          No column mapping stored for <span className="font-mono">{storeId}</span>.
        </p>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          The pipeline will try to resolve one from the store&rsquo;s Glue script on its next file.
        </p>
        <button
          onClick={startEdit}
          className="mt-4 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          Create mapping
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-theme-surface-accent border border-slate-200 dark:border-slate-700 rounded-lg">
      <div className="flex items-start justify-between gap-4 p-4 border-b border-slate-200 dark:border-slate-700">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
              Column mapping
            </h3>
            <Badge color={source.color} size="xs">
              {source.label}
            </Badge>
            {resolvable ? (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                <FaCircleCheck className="w-3.5 h-3.5" />
                Resolvable
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-rose-500 dark:text-rose-400">
                <FaTriangleExclamation className="w-3.5 h-3.5" />
                Unresolvable
              </span>
            )}
          </div>
          {mapping.glue_job && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Imported from Glue job <span className="font-mono">{mapping.glue_job}</span>
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={startEdit}
            title="Edit mapping"
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <FaPencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => setConfirmingDelete(true)}
            title="Delete mapping"
            className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400"
          >
            <FaTrash className="w-4 h-4" />
          </button>
        </div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 p-4">
        {MAPPING_FIELDS.map((field) => {
          const value = mapping[field.key];
          const isMissingRequired = field.required && !value;
          return (
            <div key={field.key} className="min-w-0">
              <dt className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                {field.label}
                {field.required && <span className="text-rose-500 ml-1">*</span>}
              </dt>
              <dd
                className={`text-sm font-mono truncate ${
                  value
                    ? 'text-slate-800 dark:text-slate-100'
                    : isMissingRequired
                      ? 'text-rose-500 dark:text-rose-400'
                      : 'text-slate-400 dark:text-slate-500'
                }`}
                title={value || ''}
              >
                {value || '—'}
              </dd>
            </div>
          );
        })}
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Delimiter
          </dt>
          <dd className="text-sm font-mono text-slate-800 dark:text-slate-100">
            {describeDelimiter(mapping.delimiter)}
          </dd>
        </div>
      </dl>

      {(mapping.warnings || []).length > 0 && (
        <ul className="px-4 pb-4 space-y-1.5">
          {mapping.warnings.map((warning, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300"
            >
              <FaTriangleExclamation className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{warning}</span>
            </li>
          ))}
        </ul>
      )}

      {confirmingDelete && (
        <div className="border-t border-slate-200 dark:border-slate-700 bg-rose-50 dark:bg-rose-900/20 p-4">
          <p className="text-sm text-rose-800 dark:text-rose-200">
            Delete this mapping? The store is handed back to Glue / import-settings resolution on
            its next file, which overwrites whatever is here today.
          </p>
          <div className="flex justify-end gap-3 mt-3">
            <button
              onClick={() => setConfirmingDelete(false)}
              className="px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-white dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                await onDelete();
                setConfirmingDelete(false);
              }}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 disabled:opacity-50"
            >
              Delete mapping
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnMappingCard;
